/**
 * Review step of the cycle configure wizard: what each track now holds, read
 * from the tracks' own records (not from the wizard's draft), and the one lock a
 * track page offers for criteria and their ranges.
 *
 * "Lock criteria & scales" calls `setTrackCriteriaAndRangesLockedViaRpc` for each
 * track that has criteria and is not locked yet — the call behind the Lock button
 * on a track's Criteria tab. It is drawn only for someone the server would let
 * lock (build contract §2.8; `canChangeTrackLock`, migration 711).
 */
import { useEffect, useMemo, useState } from 'react';
import { CheckCircle, Loader2, Lock } from 'lucide-react';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useAttributeGovernanceStore, criteriaRowsForTrack } from '../../../stores/prioritization/attributeGovernanceStore';
import { useTrackParticipantsStore } from '../../../stores/prioritization/trackParticipantsStore';
import { useCycleParticipantsStore } from '../../../stores/prioritization/cycleParticipantsStore';
import { setTrackCriteriaAndRangesLockedViaRpc } from '../../../stores/prioritization/sessionStore';
import { useCycleTracksAuthority } from '../../../hooks/prioritization/useTrackAuthority';
import { TrackWriteReport, type TrackWriteFailure } from './CycleTrackTargets';
import { useCycleOpenTracks } from '../../../hooks/prioritization/useCycleOpenTracks';

export function CycleConfigReview({ cycleId }: { cycleId: string }) {
  const { tracks, refresh: refreshTracks } = useCycleOpenTracks(cycleId);
  const attributes = useAttributesStore(s => s.attributes);
  const cycleConfigs = useAttributeGovernanceStore(s => s.cycleConfigs);
  const fetchCycleConfigs = useAttributeGovernanceStore(s => s.fetchCycleConfigs);
  const trackParticipants = useTrackParticipantsStore(s => s.participants);
  const fetchTrackParticipants = useTrackParticipantsStore(s => s.fetchParticipantsForTrack);
  const cycleParticipants = useCycleParticipantsStore(s => s.participants);
  const fetchCycleParticipants = useCycleParticipantsStore(s => s.fetchParticipantsForCycle);
  const authorityOf = useCycleTracksAuthority(cycleId);

  const trackIdsKey = tracks.map(t => t.id).join(',');
  useEffect(() => {
    void fetchCycleConfigs(cycleId);
    void fetchCycleParticipants(cycleId);
    for (const id of trackIdsKey ? trackIdsKey.split(',') : []) void fetchTrackParticipants(id);
  }, [cycleId, trackIdsKey, fetchCycleConfigs, fetchCycleParticipants, fetchTrackParticipants]);

  const labelOf = (key: string) => attributes.find(a => a.key === key)?.label ?? key;
  const cycleRoster = useMemo(
    () => new Set(cycleParticipants.filter(p => p.cycleId === cycleId && p.status === 'active').map(p => p.userId)),
    [cycleParticipants, cycleId],
  );

  const rows = tracks.map(track => {
    const criteria = criteriaRowsForTrack(cycleConfigs, track);
    const people = new Set([
      ...cycleRoster,
      ...trackParticipants.filter(p => p.trackId === track.id && p.status === 'active').map(p => p.userId),
    ]);
    return { track, criteria, accepted: criteria.filter(c => c.rangeStatus === 'accepted').length, people: people.size };
  });

  const lockable = rows.filter(r => r.track.criteriaLockedAt == null && r.criteria.length > 0);
  const lockGate = tracks[0] ? authorityOf(tracks[0]).lockGate : { allowed: false };

  const [locking, setLocking] = useState(false);
  const [report, setReport] = useState<{ saved: number; failures: TrackWriteFailure[] } | null>(null);

  const handleLock = async () => {
    setLocking(true);
    const failures: TrackWriteFailure[] = [];
    let saved = 0;
    try {
      for (const { track } of lockable) {
        const result = await setTrackCriteriaAndRangesLockedViaRpc(track.id, true);
        if (result.success) saved += 1;
        else failures.push({ trackName: track.name, what: 'the criteria & scales lock', error: result.error ?? 'The server refused the change' });
      }
      await refreshTracks();
      setReport({ saved, failures });
    } finally {
      setLocking(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-[var(--text-pri)]">Review</h3>
        <p className="text-sm text-[var(--text-sec)] mt-1">
          What each track holds now. These are the tracks&apos; own records: the cycle page and each
          track page show the same.
        </p>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-[var(--text-sec)]">This cycle has no open tracks.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map(({ track, criteria, accepted, people }) => (
            <li key={track.id} className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4 space-y-1" data-testid={`review-track-${track.id}`}>
              <div className="flex items-center justify-between gap-3">
                <h4 className="font-medium text-[var(--text-pri)]">{track.name}</h4>
                <span className="flex items-center gap-3 text-xs text-[var(--text-ter)]">
                  {track.criteriaLockedAt && (
                    <span className="flex items-center gap-1"><Lock className="h-3 w-3" aria-hidden />Criteria &amp; scales locked</span>
                  )}
                  {track.participationLockedAt && (
                    <span className="flex items-center gap-1"><Lock className="h-3 w-3" aria-hidden />People locked</span>
                  )}
                </span>
              </div>
              <p className="text-sm text-[var(--text-sec)]">
                Criteria: {criteria.length ? criteria.map(c => labelOf(c.attributeKey)).join(', ') : 'none'}
              </p>
              {criteria.length > 0 && (
                <p className="text-sm text-[var(--text-sec)]">Value ranges: {accepted} of {criteria.length} accepted</p>
              )}
              {criteria.length > 0 && (
                <p className="text-sm text-[var(--text-sec)]">
                  {/* Sessions take the weights only when every criterion has one (migration 1048). */}
                  Weights:{' '}
                  {criteria.every(c => c.weight != null)
                    ? criteria.map(c => `${labelOf(c.attributeKey)} ${c.weight}%`).join(' · ')
                    : criteria.some(c => c.weight != null)
                      ? `on ${criteria.filter(c => c.weight != null).length} of ${criteria.length} criteria, so sessions use an even split`
                      : 'none, so sessions use an even split'}
                </p>
              )}
              <p className="text-sm text-[var(--text-sec)]">People: {people}</p>
            </li>
          ))}
        </ul>
      )}

      {report && <TrackWriteReport saved={report.saved} failures={report.failures} />}

      {lockable.length > 0 && (
        lockGate.allowed ? (
          <div className="flex items-center justify-between gap-3 border-t border-[var(--rule)] pt-4">
            <p className="text-sm text-[var(--text-sec)]">
              Locking freezes each track&apos;s criteria and their ranges and keeps them as a numbered version, as the
              Lock on a track&apos;s Criteria tab does. Optional.
            </p>
            <button
              type="button"
              onClick={() => void handleLock()}
              disabled={locking}
              className="btn btn-secondary flex shrink-0 items-center gap-2 disabled:opacity-50"
            >
              {locking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              Lock criteria &amp; scales on {lockable.length} track{lockable.length === 1 ? '' : 's'}
            </button>
          </div>
        ) : (
          <p className="text-sm text-[var(--text-ter)] border-t border-[var(--rule)] pt-4">
            Locking criteria &amp; scales: {lockGate.reason}
          </p>
        )
      )}

      {lockable.length === 0 && rows.length > 0 && rows.every(r => r.track.criteriaLockedAt != null) && (
        <p className="flex items-center gap-2 text-sm text-[var(--green)]">
          <CheckCircle className="h-4 w-4" /> Criteria &amp; scales are locked on every track.
        </p>
      )}
    </div>
  );
}
